import { To } from 'react-router-dom';

import { useRequestRenewalMutation } from 'services/patientChart/patientChart';
import { RequestRenewalReqProps } from 'services/patientChart/patientChart.types';

import { setModal } from 'store/modals/modalSlice';

import { notifyError } from 'shared/Toast/Toast';

import { useAppDispatch } from 'hooks';
import { usePaywall } from 'hooks/usePaywall/usePaywall';
import { buildRenewalTarget } from 'router/renewalTargets';
import { logDataDogEvent } from 'utils/datadog';
import { handleRequestCatch } from 'utils/error';
import {
  getRenewalEligibilityError,
  getRenewalRequestPopup,
  parseRenewalPopup
} from 'utils/renewalRequest';

/**
 * The single place every renewal-start entry point branches through — My Prescriptions, the Get care
 * flow and the `action=medication-renewal` deep link. Mirrors mobile's `HandleRenewalRequestResultUseCase`
 * so the entry points cannot diverge in how they read the response.
 *
 * `POST /prescriptions/renewal/request` resolves the program's renewable set and gates eligibility +
 * readiness server-side, so there are no client-side readiness guards or hardcoded refusal copy here.
 */
export const useStartRenewal = () => {
  const dispatch = useAppDispatch();
  const { handlePaywallError } = usePaywall();
  const [requestRenewal, { isLoading }] = useRequestRenewalMutation();

  /** Logs an off-contract renewal response and still tells the patient something went wrong. */
  const reportFailure = (message: string, error?: Error) => {
    logDataDogEvent({ error, level: 'warn', message });
    notifyError('Something went wrong, please try again later');
  };

  const showPopup = (popup: NonNullable<ReturnType<typeof parseRenewalPopup>>) =>
    dispatch(setModal({ key: 'renewalRequestPopup', value: popup }));

  /**
   * The paywall outranks a popup, matching mobile: it is a typed action both clients already handle.
   * Keyed on the payload rather than the status code, so a 403 carrying both routes to the paywall.
   */
  const handleError = (error: unknown) => {
    // Guarded: `handlePaywallError` dereferences `action.paywall` / `action.link` unchecked, so even a
    // shape-validated payload gets a net here — a throw must never escape as an unhandled rejection.
    const eligibilityError = getRenewalEligibilityError(error);
    if (eligibilityError) {
      try {
        handlePaywallError(eligibilityError, { ctx: 'prescription' });
      } catch (paywallError) {
        reportFailure('Renewal request paywall action could not be handled', paywallError as Error);
      }
      return;
    }

    // A 4xx may carry a `popup` too — render it rather than falling back to the snackbar.
    const popup = getRenewalRequestPopup(error);
    if (popup) {
      showPopup(popup);
      return;
    }

    logDataDogEvent({ error: error as Error, level: 'warn', message: 'Renewal request failed' });
    handleRequestCatch(error as MessageEvent);
  };

  /**
   * Starts a renewal and resolves to the created survey's target, or `null` when there is nowhere to go
   * (refusal popup shown, paywall raised, or failure reported). Never throws — callers only navigate.
   */
  const startRenewal = async (arg: RequestRenewalReqProps): Promise<To | null> => {
    try {
      const { data } = await requestRenewal(arg).unwrap();

      // The 200 body is `surveyId` XOR `popup`. The popup goes through the same parser as the 4xx path,
      // so a half-formed payload degrades to the snackbar instead of rendering a blank sheet.
      if (data.surveyId) {
        return buildRenewalTarget({ surveyId: data.surveyId, type: 'renewal' });
      }

      const popup = parseRenewalPopup(data.popup);
      if (popup) {
        showPopup(popup);
        return null;
      }

      // Neither branch present (or an unrenderable popup) — off-contract. Never leave the tap looking
      // like a mis-click.
      reportFailure('Renewal request returned neither surveyId nor a valid popup');
      return null;
    } catch (error) {
      handleError(error);
      return null;
    }
  };

  return { isRequestingRenewal: isLoading, reportRenewalFailure: reportFailure, startRenewal };
};
